import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { clsx } from 'clsx';
import EmptyList from 'components/EmptyList/EmptyList';
import Loader from 'components/Loader/Loader';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import TableSort from 'components/Table/TableSort';
import { SORT } from 'constants/formFields';

import { MATERIALITY_ASSESSMENT_SURVEY_ANSWER_HEAD_CELLS } from '../../constants/dataSets';
import { getTextDate } from '../../utils/formattedData';
import TableMAInsightActions from './TableMAInsightActions';

import type { FieldValues, Path } from 'react-hook-form';
import type ITableMAInsightProps
  from '../../pages/MaterialAssessmentPages/MaterialAssessmentSurvey/interfaces/ITableMAInsightProps';

import styles from './scss/TableMAInsight.module.scss';

function TableMAInsight<TForm extends FieldValues>({
  insightProps,
  disableActions,
}: ITableMAInsightProps<TForm>) {
  const { t } = useTranslation();

  const emptyListText = (
    <Trans
      i18nKey="common.noResultsWithFilters"
      t={t}
      components={{
        br: <br />,
      }}
    />
  );

  const tableRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.panel}>
      <div
        className={clsx(styles.loaderWrap, insightProps.isLoading && styles.loaderWrapActive)}
        aria-hidden={!insightProps.isLoading}
      >
        <Loader
          isPageLoading={insightProps.isLoading}
          isFullLoader={false}
          classNameLoader={styles.loaderCentered}
        />
      </div>

      {!insightProps.isLoading
        && (insightProps.items.length > 0 && MATERIALITY_ASSESSMENT_SURVEY_ANSWER_HEAD_CELLS ? (
          <InfiniteScroll
            className={styles.infiniteScroll}
            dataLength={insightProps.items?.length || 0}
            next={insightProps.loadMoreQuestions}
            hasMore={insightProps.hasMore}
            loader={false}
          >
            <div ref={tableRef} className={styles.tableBodyScroll}>
              <Table className={styles.table}>
                <TableHead className={styles.header}>
                  {MATERIALITY_ASSESSMENT_SURVEY_ANSWER_HEAD_CELLS.map((col) => {
                    if (col.id === 'actions' && disableActions) {
                      return null;
                    }
                    return (
                      col.withSort ? (
                        <Controller
                          key={col.id}
                          name={SORT as Path<TForm>}
                          control={insightProps.control}
                          render={({ field: { value } }) => (
                            <TableCell head className={styles.tableCell}>
                              <TableSort
                                active={col.id === value?.orderKey}
                                onClickSort={() => insightProps.onSortChange && insightProps.onSortChange(col.id)}
                              >
                                <span>{t(col?.label || '')}</span>
                              </TableSort>
                            </TableCell>
                          )}
                        />
                      ) : (
                        <TableCell head className={styles.tableCell} key={col.id}>
                          <div>
                            <span>{t(col?.label || '')}</span>
                          </div>
                        </TableCell>
                      )
                    );
                  })}
                </TableHead>
                <TableBody className={styles.body}>
                  {!insightProps.isLoading && insightProps.items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <span className={styles.nameCell}>{item?.questionList.name}</span>
                      </TableCell>
                      <TableCell>
                        <span className={styles.description}>{item?.questionList.description}</span>
                      </TableCell>
                      <TableCell>
                        <span className={styles.question}>
                          {item.respondent ? (
                            `${item.respondent.firstName} ${item.respondent.lastName}`
                          ) : t('materialityAssessment.anonymous')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={styles.nameCell}>{item?.valueChainMap?.name || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div className={styles.statusCell}>
                          <span className={`${styles.statusText} ${styles[item?.status]}`}>
                            <span>{t(`materialityAssessment.statusOptions.${item?.status}`)}</span>
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.createdCell}>
                          <span className={styles.titleCreated}>{getTextDate(item?.createdAt)}</span>
                        </div>
                      </TableCell>
                      {!disableActions && (
                      <TableCell>
                        <div className={styles.actionCell}>
                          <TableMAInsightActions
                            surveyItem={item}
                            onDelete={insightProps.onDelete}
                            disableActions={disableActions}
                          />
                        </div>
                      </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </InfiniteScroll>
        ) : (
          <EmptyList text={emptyListText} />
        ))}
    </div>
  );
}

export default TableMAInsight;
